/**
 * @fileoverview The calendar React context and its value type, outside
 * calendar-context.tsx so that file exports only components
 * (react-refresh/only-export-components).
 */

import { createContext, type Dispatch, type SetStateAction } from "react";

import type { IEvent, IUser } from "@/calendar/interfaces";
import type { CalendarDateMode } from "@/calendar/eventAdapter";
import type {
  TBadgeVariant,
  TCalendarView,
  TVisibleHours,
  TWorkingHours,
} from "@/calendar/types";

export interface ICalendarContext {
  view: TCalendarView;
  setView: (view: TCalendarView) => void;
  selectedDate: Date;
  setSelectedDate: (date: Date | undefined) => void;
  selectedUserId: IUser["id"] | "all";
  setSelectedUserId: (userId: IUser["id"] | "all") => void;
  badgeVariant: TBadgeVariant;
  setBadgeVariant: (variant: TBadgeVariant) => void;
  users: IUser[];
  workingHours: TWorkingHours;
  setWorkingHours: Dispatch<SetStateAction<TWorkingHours>>;
  visibleHours: TVisibleHours;
  setVisibleHours: Dispatch<SetStateAction<TVisibleHours>>;
  events: IEvent[];
  /**
   * Whether events are placed on their due date, their planned window, or both
   * (MOR-696). Controlled by the header toggle; consumed by the client
   * container which re-places the event stream before windowing.
   */
  dateMode: CalendarDateMode;
  setDateMode: (mode: CalendarDateMode) => void;
  /** Fires when an event badge / agenda card / time block is clicked. */
  onEventClick: (event: IEvent) => void;
  /**
   * Fires when an empty day cell (month / year view) is clicked, or when a
   * time range is swept out via drag on the day / week grids. Used by the
   * page to open the "create key date" side panel pre-filled with the chosen
   * date and (for a dragged range) an end time.
   */
  onDateCellClick: (date: Date, endDate?: Date) => void;
  /** When true, badges render the associated job name (firm-wide calendar). */
  showJobName: boolean;
}

export const CalendarContext = createContext<ICalendarContext | null>(null);
